'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  consultationType: string;
  userEmail: string;
}

export default function SuccessModal({
  isOpen,
  onClose,
  consultationType,
  userEmail,
}: SuccessModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-text-primary/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg shadow-natural-lg max-w-md w-full p-8 animate-scale-in">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircleIcon" size={32} className="text-success" />
          </div>
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
            Request Submitted Successfully!
          </h3>
          <p className="text-text-secondary mb-6">
            Thank you for your interest in GenCarbon ESG. We&apos;ve received your consultation
            request and will get back to you shortly.
          </p>

          <div className="bg-surface rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start space-x-3 mb-3">
              <Icon name="EnvelopeIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-text-primary mb-1">
                  Confirmation Email Sent
                </p>
                <p className="text-xs text-text-secondary">{userEmail}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Icon name="ClockIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-text-primary mb-1">Expected Response Time</p>
                <p className="text-xs text-text-secondary">Within 24-48 hours</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-primary text-primary-foreground font-heading font-semibold text-sm rounded-lg hover:bg-primary/90 shadow-natural transition-all duration-300"
            >
              Continue Exploring
            </button>
            <a
              href={`https://wa.me/919876543210?text=Hi, I just submitted a consultation request for ${consultationType}. Looking forward to connecting!`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full px-6 py-3 bg-[#25D366] text-white font-heading font-semibold text-sm rounded-lg hover:bg-[#20BA5A] shadow-natural transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={20} />
              <span>Chat on WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}